import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export interface TenantCurrent {
  id: string;
  name: string;
  legalName: string | null;
  cnpj: string;
  email: string;
  phone: string | null;
  status: string;
  trialEndsAt: string | null;
  timezone: string;
  locale: string;
  pricePerVehicleCents: number;
  billingDay: number;
  createdAt: string;
  plan: { code: string; name: string; maxVehicles: number | null; priceCents: number };
}

export interface BillingPreview {
  vehicleCount: number;
  activeVehicleCount: number;
  pricePerVehicleCents: number;
  subtotalCents: number;
  totalDueCents: number;
  billingDay: number;
  nextInvoiceAt: string;
  daysUntilInvoice: number;
}

export interface TenantUsage {
  users: number;
  vehicles: number;
  drivers: number;
  activeVehicles: number;
  checklistsToday: number;
  checklistsTotal: number;
}

const KEY = {
  current: ['tenant', 'current'] as const,
  usage: ['tenant', 'usage'] as const,
  billing: ['tenant', 'billing-preview'] as const,
};

export function useTenantCurrent() {
  return useQuery({
    queryKey: KEY.current,
    queryFn: async () => {
      const { data } = await api.get<{ data: TenantCurrent }>('/tenants/current');
      return data.data;
    },
  });
}

export function useTenantUsage() {
  return useQuery({
    queryKey: KEY.usage,
    queryFn: async () => {
      const { data } = await api.get<{ data: TenantUsage }>('/tenants/current/usage');
      return data.data;
    },
  });
}

export function useUpdateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name?: string;
      legalName?: string;
      email?: string;
      phone?: string;
      timezone?: string;
      locale?: string;
    }) => {
      const { data } = await api.patch<{ data: TenantCurrent }>('/tenants/current', input);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant'] }),
  });
}

export function useBillingPreview() {
  return useQuery({
    queryKey: KEY.billing,
    queryFn: async () => {
      const { data } = await api.get<{ data: BillingPreview }>(
        '/tenants/current/billing-preview',
      );
      return data.data;
    },
  });
}

export function useUpdateBilling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { pricePerVehicleCents?: number; billingDay?: number }) => {
      const { data } = await api.patch<{ data: BillingPreview }>(
        '/tenants/current/billing',
        input,
      );
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant'] }),
  });
}
